import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaTrashAlt, FaPlus, FaArrowLeft } from 'react-icons/fa';
import UploadCertificate from '../seeker/Upload_certificate';
import { useBreakpointValue } from '@chakra-ui/react';
import axios from 'axios';

const DocumentCard = ({ name, date, id, status, onDelete }) => {
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const cancelRef = React.useRef();

  const handleDelete = () => {
    setIsAlertOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const ticketToken = sessionStorage.getItem('ticketToken');
      const response = await axios.delete(`${process.env.REACT_APP_SERVER_URL}/applicant/delete-certificate`, {
        headers: {
          Authorization: `Bearer ${ticketToken}`
        },
        data: { certificateId: id }
      });

      if (response.status === 200) {
        onDelete(id);
        alert("Certificate deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting certificate:", error);
    } finally {
      setIsAlertOpen(false);
    }
  };

  const cancelDelete = () => {
    setIsAlertOpen(false);
  };

  const handleView = async () => {
    try {
      const ticketToken = sessionStorage.getItem('ticketToken');
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/applicant/get-applicant-certificates`, {
        headers: {
          Authorization: `Bearer ${ticketToken}`
        }
      });

      if (response.status === 200) {
        const document = response.data.data.find(doc => doc._id === id);
        if (document) {
          setDocumentUrl(document.viewUrl);
          setIsViewOpen(true);
        }
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'declined':
        return 'Certificates declined, reupload needed';
      case 'pending':
        return 'Waiting for verification';
      default:
        return 'Unknown status';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'green.500';
      case 'declined':
        return 'red.500';
      case 'pending':
        return 'orange.500';
      default:
        return 'gray.500';
    }
  };

  return (
    <Box
      maxW="100%"
      minH="200px"
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      p="4"
      boxShadow="md"
      bg="white"
      position="relative"
    >
      <Flex justify="space-between" align="center">
        <Text fontWeight="bold" fontSize="lg" textAlign="left" w="full">
          {name}
        </Text>
      </Flex>
      
      <Flex align="center" mt="2">
        <Icon as={FaCalendarAlt} boxSize={4} color="gray.500" />
        <Text fontSize="sm" ml="2" color="gray.500" textAlign="left">
          {date}
        </Text>
      </Flex>

      <Text fontSize="sm" ml color={getStatusColor(status)} textAlign="left" mt="1">
        {getStatusMessage(status)}
      </Text>

      <Flex position="absolute" bottom="4" left="4" right="4" justify="space-between" align="center">
        <Icon
          as={FaTrashAlt}
          color="gray.500"
          boxSize={5}
          cursor="pointer"
          onClick={handleDelete}
        />
        <Button
          colorScheme="blue"
          bg="blue.500"
          color="white"
          size="sm"
          width="100px"
          height="32px"
          fontSize="12px"
          onClick={handleView}
        >
          View
        </Button>
      </Flex>

      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
          {documentUrl.endsWith('.png') || documentUrl.endsWith('.jpg') || documentUrl.endsWith('.jpeg') ? (
              <img src={documentUrl} alt="Document" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <iframe src={documentUrl} width="100%" height="500px" />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelDelete}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Document
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete the document: {name}?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelDelete}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const DocumentCardList = ({ onBack }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [documents, setDocuments] = useState([]);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const handleDeleteDocument = (name) => {
    setDocuments(documents.filter(doc => doc.name !== name));
  };

  const handleUploadCertificate = (certificateName, validityPeriod) => {
    if (certificateName && validityPeriod) {
      setDocuments([...documents, { name: certificateName, date: validityPeriod}]);
    }
    closeModal();
  };

  const buttonText = useBreakpointValue({ base: '', md: 'Upload Document' });
  const buttonStyle = useBreakpointValue({
    base: { bg: 'transparent', color: 'blue.500', boxSize: 6 },
    md: { bg: 'blue.500', color: 'white', width: '220px', height: '50px' }
  });

  const fetchCertificates = async () => {
    try {
      const ticketToken = sessionStorage.getItem('ticketToken');

      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/applicant/get-applicant-certificates`, {
        headers: {
          Authorization: `Bearer ${ticketToken}`
        }
      });
      if (response.status === 200) {
        console.log("Certificates fetched successfully", response.data);
        const verifiedSpecializations = response.data.data
          .filter(cert => cert.verificationStatus === 'verified')
          .map(cert => cert.specializations)
          .flat();
        console.log("Verified Specializations:", verifiedSpecializations);
        setDocuments(response.data.data.map(cert => ({
          id: cert._id,
          name: cert.certificate,
          date: cert.validityPeriod,
          status: cert.verificationStatus,
          specializations: cert.specializations
        })));
      }
    } catch (error) {
      console.error("Error fetching certificates:", error);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  return (
    <Box
      bg="gray.100"
      minH="100vh"
      p={4}
      position="relative"
      maxHeight="80vh"
      overflowY="auto"
      borderRadius="md"
    >
      <Box maxW="1200px" mx="auto" p={2} position="relative"> 
        <Flex 
          direction={{ base: "column", md: "row" }}
          justify="space-between" 
          mb={4} 
          align={{ base: "stretch", md: "center" }}
          gap={3}
        >
          <Icon
            as={FaArrowLeft}
            color="blue.500"
            boxSize={4}
            cursor="pointer"
            onClick={onBack}
          />
          <Box 
            flex="1"
            p={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            maxHeight={{ base: "100px", md: "none" }}
            overflowY="auto"
          >
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              <Text as="span" fontWeight="bold" color="blue.600">Verified Specializations:</Text>{' '}
              {[...new Set(documents
                .filter(doc => doc.status === 'verified')
                .flatMap(doc => doc.specializations))]
                .join(' • ')}
            </Text>
          </Box>
          <Button
            colorScheme="blue"
            {...buttonStyle}
            fontSize="md"
            rightIcon={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxSize={6}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.500"
              >
                <Icon as={FaPlus} boxSize={4} />
              </Box>
            }
            onClick={openModal}
            _hover={{ bg: buttonStyle.bg }}
            _focus={{ boxShadow: "none", bg: "transparent" }}
          >
            {buttonText}
          </Button>
        </Flex>

        <SimpleGrid
          columns={{ base: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          spacing={3}
        >
          {documents.map((doc, index) => (
            <DocumentCard
              key={index}
              name={doc.name}
              date={doc.date}
              status={doc.status}
              id={doc.id}
              onDelete={handleDeleteDocument}
            />
          ))}
        </SimpleGrid>

        <Modal isOpen={isOpen} onClose={closeModal} size="xl" closeOnOverlayClick={true}> 
          <ModalOverlay
            backdropFilter="blur(10px)"
            bg="rgba(0, 0, 0, 0.5)"
          />
          <ModalContent
            bg="transparent"
            boxShadow="none"
            maxWidth="500px"
            p={0}
          >
            <ModalBody p={4}>
                <UploadCertificate closeModal={handleUploadCertificate} /> 
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default DocumentCardList;